// Клиент для `/api/public/public_states` — снапшоты project'а под well.
// Типы — единый источник правды: ts-rs-генерация из
// `crates/backend/web_api_types/src/public_state.rs` (alias @webtypes, см.
// vite.config.ts / tsconfig.app.json). Регенерация — `cargo test -p web_api_types`.
//
// ⚠ URL-коллизия: `/api/public/states` (другой эндпоинт) — это main_app states
// (linked main_app accounts), см. api/states.ts. А вот `/api/public/public_states`
// — это собственно public_state снапшоты (то, чем рулит PublicStatesPage).
//
// MD/scope-инварианты валидируются и на сервере, и здесь:
//   * casings: md_end_m > md_start_m
//   * muds:    scope ∈ {'outer','inner'}
//
// PUT replace-эндпоинты заменяют ВЕСЬ массив привязок одной транзакцией;
// сервер сам проставит place = индекс.

import type { PublicStateSummaryDto } from '@webtypes/PublicStateSummaryDto'
import type { PublicStateCasingDto } from '@webtypes/PublicStateCasingDto'
import type { PublicStateMudDto } from '@webtypes/PublicStateMudDto'
import type { PublicStateDetailDto } from '@webtypes/PublicStateDetailDto'
import type { PublicStateCasingInput } from '@webtypes/PublicStateCasingInput'
import type { PublicStateMudInput } from '@webtypes/PublicStateMudInput'
import type { CreatePublicStateBody } from '@webtypes/CreatePublicStateBody'
import type { UpdatePublicStateHeaderBody } from '@webtypes/UpdatePublicStateHeaderBody'
import type { ReplaceStateCasingsBody } from '@webtypes/ReplaceStateCasingsBody'
import type { ReplaceStateMudsBody } from '@webtypes/ReplaceStateMudsBody'

export type {
  PublicStateSummaryDto,
  PublicStateCasingDto,
  PublicStateMudDto,
  PublicStateDetailDto,
  PublicStateCasingInput,
  PublicStateMudInput,
  CreatePublicStateBody,
  UpdatePublicStateHeaderBody,
  ReplaceStateCasingsBody,
  ReplaceStateMudsBody,
}

export const MUD_SCOPES = [
  { id: 'outer', label: 'Затрубное (outer)' },
  { id: 'inner', label: 'Внутри БК (inner)' },
] as const

const BASE = '/api/public/public_states'

async function ensureOk(resp: Response): Promise<void> {
  if (!resp.ok) {
    const text = await resp.text().catch(() => '')
    throw new Error(`${resp.status} ${resp.statusText}${text ? ': ' + text : ''}`)
  }
}

export async function listPublicStates(): Promise<PublicStateSummaryDto[]> {
  const resp = await fetch(BASE, { credentials: 'same-origin' })
  await ensureOk(resp)
  return resp.json()
}

export async function getPublicState(id: number): Promise<PublicStateDetailDto> {
  const resp = await fetch(`${BASE}/${id}`, { credentials: 'same-origin' })
  await ensureOk(resp)
  return resp.json()
}

export async function createPublicState(body: CreatePublicStateBody): Promise<PublicStateDetailDto> {
  const resp = await fetch(BASE, {
    method:      'POST',
    credentials: 'same-origin',
    headers:     { 'content-type': 'application/json' },
    body:        JSON.stringify(body),
  })
  await ensureOk(resp)
  return resp.json()
}

export async function updatePublicStateHeader(
  id: number,
  body: UpdatePublicStateHeaderBody,
): Promise<PublicStateSummaryDto> {
  const resp = await fetch(`${BASE}/${id}`, {
    method:      'PATCH',
    credentials: 'same-origin',
    headers:     { 'content-type': 'application/json' },
    body:        JSON.stringify(body),
  })
  await ensureOk(resp)
  return resp.json()
}

export async function deletePublicState(id: number): Promise<void> {
  const resp = await fetch(`${BASE}/${id}`, {
    method:      'DELETE',
    credentials: 'same-origin',
  })
  await ensureOk(resp)
}

export async function replaceStateCasings(
  id: number,
  body: ReplaceStateCasingsBody,
): Promise<PublicStateDetailDto> {
  const resp = await fetch(`${BASE}/${id}/casings`, {
    method:      'PUT',
    credentials: 'same-origin',
    headers:     { 'content-type': 'application/json' },
    body:        JSON.stringify(body),
  })
  await ensureOk(resp)
  return resp.json()
}

export async function replaceStateMuds(
  id: number,
  body: ReplaceStateMudsBody,
): Promise<PublicStateDetailDto> {
  const resp = await fetch(`${BASE}/${id}/muds`, {
    method:      'PUT',
    credentials: 'same-origin',
    headers:     { 'content-type': 'application/json' },
    body:        JSON.stringify(body),
  })
  await ensureOk(resp)
  return resp.json()
}
